/**
 * The Odżywianie stage card — the Pomiary stage's pattern (`DetailTab.module.css`): what the target
 * is, one sentence about the range, the nutrient switch with the weight toggle, the three range
 * presets, and the plot. The plot's box is reserved in every state, so the answer landing changes
 * ink and never geometry.
 */

import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import ember from '~/shared/ui/emberButton.module.css';
import { EmptyState } from '~/shared/ui/EmptyState';
import { TrendMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { NUTRIENTS, NUTRITION_SPANS, type Nutrient, type NutritionSpan } from '../model/nutrition';
import { legendLabel, type NutritionStageView } from '../model/nutritionStage';

import detail from './DetailTab.module.css';
import { NutritionChart } from './NutritionChart';
import { CalculatorGlyph, MacroGlyph } from './NutritionGlyphs';
import styles from './NutritionTab.module.css';

const KEY = 'web:clientDetail.nutrition';

interface NutritionStageProps {
  readonly view: NutritionStageView | null;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly span: NutritionSpan;
  readonly onSpan: (span: NutritionSpan) => void;
  readonly nutrient: Nutrient;
  readonly onNutrient: (nutrient: Nutrient) => void;
  readonly weight: boolean;
  readonly onWeight: (on: boolean) => void;
  readonly onEditTarget: () => void;
}

export function NutritionStage({
  view,
  gate,
  failed,
  onRetry,
  span,
  onSpan,
  nutrient,
  onNutrient,
  weight,
  onWeight,
  onEditTarget,
}: NutritionStageProps) {
  const { t } = useTranslation();
  const answered = !gate.waiting && !failed && view !== null;
  const hasTarget = answered && view.target !== null;

  return (
    <>
      <div className={detail.stageHead}>
        <div className={styles.say} aria-busy={gate.waiting}>
          {gate.waiting ? (
            <>
              <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
              {gate.skeleton ? (
                <span className={styles.sayWait}>
                  <i className={`${String(skel.bar)} ${String(styles.skelNow)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelSentence)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelSub)}`} />
                </span>
              ) : null}
            </>
          ) : !answered ? null : (
            <div className={gate.reveal}>
              <p className={styles.now}>
                {view.target === null ? (
                  <>
                    {t(`${KEY}.noTarget`)}
                    <b className={styles.nowValue}>{t(`${KEY}.noTargetValue`)}</b>
                  </>
                ) : (
                  <>
                    {t(`${KEY}.targetSince`, { date: view.target.since })}
                    <b className={styles.nowValue}>{view.target.energy}</b>
                    {view.target.macros.map((macro) => (
                      <span className={styles.nowMacro} key={macro.nutrient}>
                        <MacroGlyph nutrient={macro.nutrient} />
                        <span className={hidden.srOnly}>{t(`${KEY}.nutrient.${macro.nutrient}`)}</span>
                        <strong className={styles.nowMacroValue}>{macro.value ?? '—'}</strong>
                      </span>
                    ))}
                  </>
                )}
              </p>
              <p className={styles.sentence}>
                {view.headline}
                <span className={styles.sentenceSpan}> · {view.spanWords}</span>
              </p>
              <p className={styles.sub}>{view.summary}</p>
            </div>
          )}
        </div>
        <button
          className={
            !answered
              ? `${String(ghost.ghost)} ${String(detail.measureButton)}`
              : hasTarget
                ? `${String(ghost.ghost)} ${String(detail.measureButton)} ${String(styles.changeTarget)}`
                : `${String(ember.ember)} ${String(ember.flat)} ${String(styles.setTarget)}`
          }
          disabled={!answered}
          onClick={onEditTarget}
          type="button"
        >
          <CalculatorGlyph />
          {t(hasTarget || !answered ? `${KEY}.changeTarget` : `${KEY}.setTarget`)}
        </button>
      </div>

      <div className={styles.toolbar}>
        <div className={detail.lineRow}>
          <span className={detail.lineLabel}>{t(`${KEY}.chartLabel`)}</span>
          <RadioGroup
            aria-label={t(`${KEY}.nutrientSwitch`)}
            className={detail.seg}
            onValueChange={(next) => {
              const chosen = NUTRIENTS.find((option) => option === next);
              if (chosen !== undefined) onNutrient(chosen);
            }}
            value={nutrient}
          >
            {NUTRIENTS.map((option) => (
              <Radio.Root
                className={`${String(detail.segOption)} ${String(styles.segWithGlyph)}`}
                key={option}
                value={option}
              >
                <MacroGlyph nutrient={option} small />
                {t(`${KEY}.nutrient.${option}`)}
              </Radio.Root>
            ))}
          </RadioGroup>
          <button
            aria-pressed={weight}
            className={`${String(detail.toggle)} ${weight ? String(detail.toggleOn) : String(styles.weightOff)}`}
            onClick={() => {
              onWeight(!weight);
            }}
            type="button"
          >
            <i aria-hidden="true" className={styles.weightDash} />
            {t(`${KEY}.weightToggle`)}
          </button>
        </div>
        <div className={detail.presets} role="group" aria-label={t(`${KEY}.rangesLabel`)}>
          {NUTRITION_SPANS.map((option) => (
            <button
              aria-pressed={span === option}
              className={`${String(detail.preset)} ${span === option ? String(detail.presetOn) : ''}`}
              key={option}
              onClick={() => {
                onSpan(option);
              }}
              type="button"
            >
              <i aria-hidden="true" className={detail.presetDot} />
              {t(`${KEY}.span.${option}`)}
            </button>
          ))}
        </div>
      </div>

      {failed ? (
        <div className={styles.plotReserve}>
          <EmptyState
            mark={<TrendMarkIcon />}
            title={
              <span>
                {t(`${KEY}.failed`)}{' '}
                <button className={inlineButton.inlineTextButton} onClick={onRetry} type="button">
                  {t('web:clientDetail.states.retry')}
                </button>
              </span>
            }
            tone="neutral"
          />
        </div>
      ) : gate.waiting || view === null ? (
        <div aria-busy="true" className={styles.plotReserve}>
          <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
          {gate.skeleton ? <i className={`${String(skel.bar)} ${String(styles.skelPlot)}`} /> : null}
        </div>
      ) : (
        <>
          <NutritionChart
            chart={view.chart}
            className={gate.reveal}
            legend={view.chart.legend.map((key) => ({ key, label: legendLabel(key, nutrient, t) }))}
            tableHeads={{ when: t(`${KEY}.colDay`), reading: t(`${KEY}.nutrient.${nutrient}`) }}
          />
        </>
      )}
    </>
  );
}
